import type { Context } from 'react';
import { createContext } from 'react';

import { useMetrics as useOtelMetrics } from '@cognite/telemetry';
import { captureException } from '@sentry/react';

import { makeToast } from '@cognite/cogs-lab-v11';
import { useMetrics } from '@cognite/metrics';

import { useInFieldMetrics } from '../../../../hooks/use-infield-metrics';
import { useCurrentUserContext } from '../../../../providers/current-user-provider/current-user-provider';
import { useFDMServices } from '../../../../providers/fdm-services/fdm-services.context';
import { useNetworkStatusContext } from '../../../../providers/network-status-provider/network-status-provider';
import type { ChecklistService } from '../../checklist-service';

export type FDMServicesChecklistType = () => {
  checklistService: {
    updateChecklists: InstanceType<typeof ChecklistService>['updateChecklists'];
  };
};

export interface UseChecklistUpsertMutationContextInterface {
  useFDMServices: FDMServicesChecklistType;
  useCurrentUserContext: () => Pick<
    ReturnType<typeof useCurrentUserContext>,
    'user'
  >;
  useNetworkStatusContext: () => Pick<
    ReturnType<typeof useNetworkStatusContext>,
    'isOnline'
  >;
  useMetrics: (
    ...args: Parameters<typeof useMetrics>
  ) => Pick<ReturnType<typeof useMetrics>, 'track'>;
  useOtelMetrics: () => Pick<
    ReturnType<typeof useOtelMetrics>,
    'appDataLoadTimeHistogram'
  >;
  useInFieldMetrics: typeof useInFieldMetrics;
  makeToast: typeof makeToast;
  captureException: typeof captureException;
}

const getDefaultProperties =
  (): UseChecklistUpsertMutationContextInterface => ({
    useFDMServices,
    useCurrentUserContext,
    useNetworkStatusContext,
    useMetrics,
    useOtelMetrics,
    useInFieldMetrics,
    makeToast,
    captureException,
  });

let context: Context<UseChecklistUpsertMutationContextInterface>;

export const getChecklistUpsertMutationContext = () => {
  if (!context) {
    context = createContext(getDefaultProperties());
  }

  return context;
};
